/**
 * NavigationManager - Orchestrates floor plan and map overlays
 *
 * Responsibilities:
 * - Decide which overlay type to render based on config
 * - Handle stage navigation events
 * - Manage overlay lifecycle
 * - Coordinate between viewer and navigation UI
 */

import type {
  NavigationConfig,
  ExperienceStage,
  ExperienceConfig
} from '../types/config';
import { FloorPlanOverlay } from './FloorPlanOverlay';
import { MapOverlay } from './MapOverlay';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('NavigationManager');

export interface NavigationManagerEvents {
  onNavigateToStage: (stageId: string) => void;
}

export class NavigationManager {
  private container: HTMLElement;
  private config: ExperienceConfig | null = null;
  private events: NavigationManagerEvents;

  private floorPlanOverlay: FloorPlanOverlay | null = null;
  private mapOverlay: MapOverlay | null = null;
  private isInitialized: boolean = false;

  constructor(container: HTMLElement, events: NavigationManagerEvents) {
    this.container = container;
    this.events = events;
  }

  /**
   * Initialize navigation with experience config
   */
  public async initialize(config: ExperienceConfig): Promise<void> {
    this.config = config;

    // Clean up any existing overlays
    this.dispose();

    // Check if navigation is configured
    if (!config.navigation || config.navigation.type === 'none') {
      this.isInitialized = true;
      return;
    }

    const navConfig = config.navigation;
    const stages = config.stages;

    // Create the appropriate overlay based on type
    if (navConfig.type === 'floorplan') {
      await this.initializeFloorPlan(navConfig, stages);
    } else if (navConfig.type === 'map') {
      await this.initializeMap(navConfig, stages);
    }

    this.isInitialized = true;
  }

  /**
   * Initialize floor plan overlay
   */
  private async initializeFloorPlan(
    navConfig: NavigationConfig,
    stages: ExperienceStage[]
  ): Promise<void> {
    // Validate floor plan config
    if (!navConfig.floorPlan && (!navConfig.floorPlans || navConfig.floorPlans.length === 0)) {
      logger.warn('Navigation type is floorplan but no floorPlan config provided');
      return;
    }

    try {
      this.floorPlanOverlay = new FloorPlanOverlay(
        this.container,
        navConfig,
        stages,
        {
          onMarkerClick: (stageId) => this.handleMarkerClick(stageId),
          onMarkerHover: (stageId) => this.handleMarkerHover(stageId),
          onExpandToggle: (expanded) => this.handleExpandToggle(expanded),
          onMinimapClick: () => this.handleMinimapClick(),
        }
      );

      this.floorPlanOverlay.render();
      logger.info('Floor plan overlay initialized');
    } catch (error) {
      logger.error('Failed to initialize floor plan overlay:', error);
      // Gracefully degrade - navigation just won't be available
      this.floorPlanOverlay = null;
      // Don't throw - let the viewer continue without navigation
    }
  }

  /**
   * Initialize map overlay with error handling
   */
  private async initializeMap(
    navConfig: NavigationConfig,
    stages: ExperienceStage[]
  ): Promise<void> {
    // Helper to check if coordinates are valid (not 0,0, NaN, or undefined)
    const isValidPosition = (pos?: { lat: number; lng: number } | null): boolean => {
      if (!pos) return false;
      if (typeof pos.lat !== 'number' || typeof pos.lng !== 'number') return false;
      if (isNaN(pos.lat) || isNaN(pos.lng)) return false;
      // Treat (0, 0) as "not set" since it's in the ocean and unlikely to be intentional
      return pos.lat !== 0 || pos.lng !== 0;
    };

    // Validate markers have valid geo positions or stages have valid locations
    const hasValidGeoData = navConfig.markers.some(m => {
      const geoPos = m.geoPosition;
      const stageLocation = stages.find(s => s.id === m.stageId)?.location;
      return isValidPosition(geoPos) || isValidPosition(stageLocation);
    });

    if (!hasValidGeoData) {
      logger.warn('Navigation type is map but no valid geo positions found (all are 0,0 or undefined)');
      return;
    }

    try {
      this.mapOverlay = new MapOverlay(
        this.container,
        navConfig,
        stages,
        {
          onMarkerClick: (stageId) => this.handleMarkerClick(stageId),
          onMarkerHover: (stageId) => this.handleMarkerHover(stageId),
          onExpandToggle: (expanded) => this.handleExpandToggle(expanded),
          onMinimapClick: () => this.handleMinimapClick(),
        }
      );

      await this.mapOverlay.render();
      logger.info('Map overlay initialized');
    } catch (error) {
      logger.error('Failed to initialize map overlay:', error);
      // Common causes: Leaflet CDN failed to load, invalid tile layer config, network error
      this.mapOverlay = null;
      // Don't throw - let the viewer continue without navigation
      // The map just won't be visible but the experience will still work
    }
  }

  /**
   * Handle marker click - navigate to stage
   */
  private handleMarkerClick(stageId: string): void {
    this.events.onNavigateToStage(stageId);
  }

  /**
   * Handle marker hover
   */
  private handleMarkerHover(_stageId: string | null): void {
    // Could add preview or highlight functionality here
  }

  /**
   * Handle expand/collapse toggle
   */
  private handleExpandToggle(_expanded: boolean): void {
    // Could pause/resume viewer or add other effects
  }

  /**
   * Handle minimap click - dispatch event to open stage selector
   */
  private handleMinimapClick(): void {
    const navType = this.config?.navigation?.type || 'none';
    document.dispatchEvent(new CustomEvent('navigation:minimapClick', {
      detail: { navType }
    }));
  }

  /**
   * Update current stage indicator on overlays
   */
  public updateCurrentStage(stageId: string): void {
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.updateCurrentStage(stageId);
    }

    if (this.mapOverlay) {
      this.mapOverlay.updateCurrentStage(stageId);
    }
  }

  /**
   * Update current stage by index
   */
  public updateCurrentStageByIndex(stageIndex: number): void {
    if (!this.config?.stages || stageIndex < 0 || stageIndex >= this.config.stages.length) {
      return;
    }

    const stageId = this.config.stages[stageIndex].id;
    this.updateCurrentStage(stageId);
  }

  /**
   * Check if navigation is enabled
   */
  public hasNavigation(): boolean {
    return this.config?.navigation?.type !== undefined &&
           this.config.navigation.type !== 'none';
  }

  /**
   * Get navigation type
   */
  public getNavigationType(): 'floorplan' | 'map' | 'none' {
    return this.config?.navigation?.type || 'none';
  }

  /**
   * Expand the navigation overlay
   */
  public expand(): void {
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.setExpanded(true);
    }
    if (this.mapOverlay) {
      this.mapOverlay.setExpanded(true);
    }
  }

  /**
   * Collapse the navigation overlay
   */
  public collapse(): void {
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.setExpanded(false);
    }
    if (this.mapOverlay) {
      this.mapOverlay.setExpanded(false);
    }
  }

  /**
   * Check if overlay is expanded
   */
  public isExpanded(): boolean {
    if (this.floorPlanOverlay) {
      return this.floorPlanOverlay.isCurrentlyExpanded();
    }
    if (this.mapOverlay) {
      return this.mapOverlay.isCurrentlyExpanded();
    }
    return false;
  }

  /**
   * Toggle expanded state
   */
  public toggleExpanded(): void {
    if (this.isExpanded()) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Check if initialized
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Dispose and clean up
   */
  public dispose(): void {
    if (this.floorPlanOverlay) {
      this.floorPlanOverlay.dispose();
      this.floorPlanOverlay = null;
    }

    if (this.mapOverlay) {
      this.mapOverlay.dispose();
      this.mapOverlay = null;
    }

    this.isInitialized = false;
  }
}
